/**
 * Images Page
 * ===========
 * Gallery view of all product images across the inventory.
 */

import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Image as ImageIcon, ChevronLeft, ChevronRight, Filter, X } from 'lucide-react'
import Button from '@/components/Button'
import { Card, CardContent, CardHeader } from '@/components/Card'
import { useImageGallery } from '@/hooks/useImageGallery'
import { useCategories } from '@/hooks/useCategories'
import type { ImageGalleryParams, CategoryTree } from '@/types'

// Helper to get image URL for display
function getImageDisplayUrl(imageUrl: string, productId: number, imageId: number): string {
  // If it's already a full URL, use it directly
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl
  }
  // Local uploads - use ID-based endpoint
  return `/api/products/${productId}/image/${imageId}/file`
}

// Flatten category tree for dropdown
function flattenCategories(categories: CategoryTree[], depth = 0): { id: number; name: string; depth: number }[] {
  const result: { id: number; name: string; depth: number }[] = []
  for (const cat of categories) {
    result.push({ id: cat.id, name: cat.name, depth })
    if (cat.children && cat.children.length > 0) {
      result.push(...flattenCategories(cat.children, depth + 1))
    }
  }
  return result
}

export default function Images() {
  const [params, setParams] = useState<ImageGalleryParams>({
    page: 1,
    limit: 40,
    include_subcategories: true,
  })
  const [showFilters, setShowFilters] = useState(false)

  // Fetch images
  const { data, isLoading, error } = useImageGallery(params)
  const images = data?.data ?? []
  const meta = data?.meta

  // Fetch categories for filter
  const { data: categoriesData } = useCategories(true)

  // Memoize categories to stabilize reference
  const categories = useMemo(() => (categoriesData?.data ?? []) as CategoryTree[], [categoriesData?.data])

  // Flatten categories for dropdown
  const flatCategories = useMemo(() => flattenCategories(categories), [categories])

  // Get category name for display
  const selectedCategoryName = useMemo(() => {
    if (!params.category_id) return null
    const cat = flatCategories.find(c => c.id === params.category_id)
    return cat?.name ?? null
  }, [params.category_id, flatCategories])

  const handlePageChange = (newPage: number) => {
    setParams(prev => ({ ...prev, page: newPage }))
  }

  const handleCategoryChange = (categoryId: number | null) => {
    setParams(prev => ({ ...prev, category_id: categoryId, page: 1 }))
  }

  const clearFilters = () => {
    setParams(prev => ({ ...prev, category_id: null, page: 1 }))
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400">Error loading images</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Images</h1>
          <p className="text-gray-500 dark:text-gray-400">
            {meta ? `${meta.total} image${meta.total !== 1 ? 's' : ''}` : 'Loading...'}
            {selectedCategoryName && ` in ${selectedCategoryName}`}
          </p>
        </div>
        <Button
          variant={showFilters || params.category_id ? 'primary' : 'secondary'}
          icon={<Filter size={16} />}
          onClick={() => setShowFilters(!showFilters)}
        >
          Filter
          {params.category_id && (
            <span className="ml-1 px-1.5 py-0.5 text-xs bg-white/20 rounded">1</span>
          )}
        </Button>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <Card>
          <CardContent>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category
                </label>
                <select
                  value={params.category_id ?? ''}
                  onChange={(e) => handleCategoryChange(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">All Categories</option>
                  {flatCategories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {'  '.repeat(cat.depth)}{cat.name}
                    </option>
                  ))}
                </select>
              </div>
              {params.category_id && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="mt-6"
                >
                  <X size={16} className="mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Image grid */}
      <Card>
        <CardHeader title="Product Images" />
        <CardContent>
          {isLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {Array.from({ length: 12 }).map((_, i) => (
                <div
                  key={i}
                  className="aspect-square rounded-lg bg-gray-200 dark:bg-gray-700 animate-pulse"
                />
              ))}
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-12">
              <ImageIcon className="mx-auto text-gray-300 dark:text-gray-600 mb-4" size={48} />
              <p className="text-gray-500 dark:text-gray-400">
                {params.category_id ? 'No images in this category' : 'No images yet'}
              </p>
              <Link
                to="/products/new"
                className="text-primary-600 dark:text-primary-400 hover:underline mt-2 inline-block"
              >
                Add a product with images
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {images.map((image) => (
                <Link
                  key={image.id}
                  to={`/products/${image.product_product_id}`}
                  className="group relative aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 hover:ring-2 hover:ring-primary-500 transition-all"
                >
                  <img
                    src={getImageDisplayUrl(image.image_url, image.product_id, image.id)}
                    alt={image.alt_text || image.product_name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    loading="lazy"
                  />
                  {/* Overlay with product name on hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-0 left-0 right-0 p-2">
                      <p className="text-white text-sm font-medium truncate">
                        {image.product_name}
                      </p>
                      <p className="text-white/70 text-xs">
                        {image.product_product_id}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>

        {/* Pagination */}
        {meta && meta.total_pages > 1 && (
          <div className="px-6 py-4 border-t dark:border-gray-700 flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Showing {(meta.page - 1) * meta.limit + 1} to{' '}
              {Math.min(meta.page * meta.limit, meta.total)} of {meta.total}
            </p>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={!meta.has_prev}
                onClick={() => handlePageChange(meta.page - 1)}
              >
                <ChevronLeft size={16} />
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={!meta.has_next}
                onClick={() => handlePageChange(meta.page + 1)}
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
